import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { getSnapshot, Instance } from 'mobx-state-tree';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import MultiCalendar, { dateFormatMultiCalendar, MultiCalendarSelectionType } from '../common/calendar/MultiCalendar';
import { CalendarPerfMonitor } from '../common/calendar/CalendarPerfMonitor';
import { Pharmacy } from '../../store/models/Pharmacy';
import { Secretariat } from '../../store/models/Secretariat';
import { DutyAssignment } from '../../store/models/DutyAssignment';
import { BlackPoint } from '../../store/models/BlackPoint';
import moment from 'moment';
import { BankHolidayDate } from '../../store/models/BankHolidayDate';
import { DutyOffRequest } from '../../store/models/DutyOffRequest';
import { Button, Classes, Dialog, Intent, Position, Tooltip } from '@blueprintjs/core';
import { TLCode } from '../../common/enums';
import styled from '@emotion/styled';
import LeaveRouteDirtyWrapper from '../common/LeaveRouteDirtyWrapper';
import { SystemStatus } from '../../store/models/SystemStatus';
import {
  UiStore,
  ModalStore,
  AuthenticationStore,
  SecretariatStore,
  PharmacyDetailedStore,
  PharmacyStore,
  GroupStore,
  DutyAssignmentStore,
  ApplicationStore,
} from '../../store';
import { Link } from 'react-router-dom';
import { Page } from '../../common/enums';
import { TranslatedMessage } from '../common/translation';
import ReactToPrint from 'react-to-print';
import { User } from '../../store/models/User';
import { PharmacyDetails } from '../../store/models';
import { DutyToSendToAnotherCalendar } from '../../store/interfaces/DutyToSend';
import { ExclusionDetails, ExclusionDetailsDialog } from '../common/calendar/dialog/ExclusionDetailsDialog';
import { TransferDutiesDialog } from '../TransferDuties/TransferDutiesDialog';
import { flashingButton } from '../common/styledComponents/StyledClasses';
import {
  ConflictDate,
  ConflictingDatesDialog,
  DateTypeOfActivity,
  DutyType,
} from '../common/calendar/dialog/ConflictingDatesDialog';
import PharmacyExchangeHistory from './PharmacyExchangeHistory';
import { IconNames } from '@blueprintjs/icons';
import { number } from 'mobx-state-tree/dist/internal';
import { CandidatesDialog } from '../common/calendar/dialog/CandidatesDialog';

const FormDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 95%;
  margin-top: 10px;
`;

const DivRight = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 5px;
  .bp3-icon {
    margin-right: 3px;
  }
  margin-left: auto;
`;

interface PharmacyDutiesCalendarProps extends WithTranslateProps {
  bankHolidayDateList: Array<Instance<typeof BankHolidayDate>>;
  blackPointList: Array<Instance<typeof BlackPoint>>;
  dutyAssignments: Array<Instance<typeof DutyAssignment>>;
  dutyOffRequests: Array<Instance<typeof DutyOffRequest>>;
  pharmacyDetails?: Instance<typeof PharmacyDetails>;
  saveCalendarChange: (data: any) => void;
  bindCancelMethod?: (method: () => void) => void;
  onCalendarMove?: (fly: number, sly: number) => void;
  pharmacyId?: number;
  systemStatus: Array<Instance<typeof SystemStatus>>;
  isSecretariat?: boolean;
  fly: number;
  sly: number;
  rowCount?: number;
  hideLegend?: boolean;
  synchronizedScrolling?: boolean;
  UiStore?: Instance<typeof UiStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;
  PharmacyDetailedStore?: Instance<typeof PharmacyDetailedStore>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  DutyAssignmentStore?: Instance<typeof DutyAssignmentStore>;
  onTypeChange?: (type: MultiCalendarSelectionType | undefined) => void;
  selectedType?: MultiCalendarSelectionType | undefined;
  separateToolbox?: boolean;
  pharmacyList?: any;
  onPharmacyChange?: (selectedOption: any) => void;
  onOtherPharmacyChange?: (otherPharmacyId: Instance<typeof number> | undefined) => void;
  inMultiCalendar?: boolean;
  ModalStore?: Instance<typeof ModalStore>;
  GroupStore?: Instance<typeof GroupStore>;
  ApplicationStore?: Instance<typeof ApplicationStore>;
  userList?: Array<Instance<typeof User>>;
  onModification?: () => void;
  onDutyToSendToAnotherPharmacy?: (date: Date, dutyType: 'NIGHT' | 'DAY' | 'DAYNIGHT', pharmacyId: number) => void;
  otherPharmacyBusyDates?: Array<Date>;
  dutySentToAnotherPharmacy?: DutyToSendToAnotherCalendar;
  onDutySentConsumed?: () => void;
  groupIdToLimitTo?: number;
  exchangeHistoryList?: any[];
}

interface PharmacyDutiesCalendarState {
  newBlackPoints: string[];
  newBlackPointsEconomic: string[];
  newBlackPointsHealth: string[];
  newBlackPointsSpecial: string[];
  newDayDuties: string[];
  newDayNightDuties: string[];
  newNightDuties: string[];
  deletedBlackPoints: string[];
  deletedBlackPointsEconomic: string[];
  deletedBlackPointsHealth: string[];
  deletedBlackPointsSpecial: string[];
  deletedDutyOffRequests: string[];
  deletedDayDuties: string[];
  deletedDayNightDuties: string[];
  deletedNightDuties: string[];
  deletedExemptions: string[];
  comment: string;
  selectedType?: MultiCalendarSelectionType;
  user?: Instance<typeof User>;
  pharmacy?: Instance<typeof Pharmacy>;
  secretariat?: Instance<typeof Secretariat>;
  exclusionDatas: ExclusionDetails[];
  isExclusionLoading: boolean;
  isTransferDutiesDialogOpen: boolean;
  isConflictingDatesDialogOpen: boolean;
  isFormSending: boolean;
  popupExchangeHistoryVisible: boolean;
  showCandidates: boolean;
  canBeReopen: boolean;
  selectedPharmacy: Instance<typeof PharmacyDetails> | undefined;
  selectedDate: string | undefined;
  availableCandidatesPharmacyList: Instance<typeof PharmacyDetails>[];
  selectedCandidatesDialogDate: string | undefined;
}

@inject(
  'UiStore',
  'AuthenticationStore',
  'SecretariatStore',
  'PharmacyDetailedStore',
  'GroupStore',
  'DutyAssignmentStore',
  'ApplicationStore',
  'PharmacyStore',
)
@observer
class PharmacyDutiesCalendar extends React.Component<PharmacyDutiesCalendarProps, PharmacyDutiesCalendarState> {
  state = {
    deletedBlackPoints: [],
    deletedBlackPointsEconomic: [],
    deletedBlackPointsHealth: [],
    deletedBlackPointsSpecial: [],
    deletedDutyOffRequests: [],
    deletedDayDuties: [],
    deletedDayNightDuties: [],
    deletedNightDuties: [],
    deletedExemptions: [],
    newBlackPoints: [],
    newBlackPointsEconomic: [],
    newBlackPointsHealth: [],
    newBlackPointsSpecial: [],
    newDayDuties: [],
    newNightDuties: [],
    newDayNightDuties: [],
    comment: '',
    selectedType: this.props.selectedType ? this.props.selectedType : undefined,
    user:
      this.props.userList && this.props.pharmacyDetails
        ? this.props.userList!.filter((u) => u.userId == this.props.pharmacyDetails!['userId'])[0]
        : undefined,
    pharmacy: undefined,
    exclusionDatas: [],
    isExclusionLoading: false,
    isTransferDutiesDialogOpen: false,
    isConflictingDatesDialogOpen: false,
    isFormSending: false,
    popupExchangeHistoryVisible: false,
    showCandidates: false,
    canBeReopen: false,
    selectedPharmacy: undefined,
    selectedDate: undefined,
    availableCandidatesPharmacyList: [],
    selectedCandidatesDialogDate: undefined,
  } as PharmacyDutiesCalendarState;

  _isMounted = false;
  private _perfRenderStart = 0;

  async componentDidMount() {
    this._isMounted = true;
    this._isMounted && this.props.GroupStore!.checkListLoaded();
    if (this.props.pharmacyId) {
      await this.props.PharmacyDetailedStore!.ensureLoaded(this.props.pharmacyId);
      const pharmacy = this.props.PharmacyDetailedStore!.getById(this.props.pharmacyId)!;
      this._isMounted && this.setState({ pharmacy: pharmacy });
      if (pharmacy != undefined && pharmacy.secretariatId != undefined) {
        await this.props.SecretariatStore!.ensureLoaded(pharmacy!.secretariatId!);
        const secretariat = this.props.SecretariatStore!.getById(pharmacy!.secretariatId!)!;
        this._isMounted && this.setState({ secretariat: secretariat });
      }
    }
    if (this.props.bindCancelMethod) {
      this.props.bindCancelMethod!(this.cancelChange);
    }
    CalendarPerfMonitor.accumulate('[PDC] render→mount cycle', performance.now() - this._perfRenderStart);
    CalendarPerfMonitor.report('Calendar — full render cycle (mount)');
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps: PharmacyDutiesCalendarProps) {
    // Fires after MultiCalendar.componentDidUpdate — captures the full render-cycle cost.
    CalendarPerfMonitor.accumulate('[PDC] render→update cycle', performance.now() - this._perfRenderStart);
    CalendarPerfMonitor.report('Calendar — full render cycle (update)');

    if (this.props.selectedType !== this.state.selectedType) this.setState({ selectedType: this.props.selectedType });
  }

  onTypeChange = (type: MultiCalendarSelectionType) => {
    if (this.state.selectedType !== type) {
      this.setState({ selectedType: type });
      if (this.props.onTypeChange) this.props.onTypeChange(type);
    }
  };

  onDatesChange = (changedData: any) => {
    this.setState({ ...changedData });
    if (this.props.onModification) this.props.onModification();
  };

  onCalendarMove = (fly: number, sly: number) => {
    if (this.props.onCalendarMove) this.props.onCalendarMove(fly, sly);
  };

  blackPointReducer = (dates: string[], blackPoint: any) => {
    const snap = getSnapshot<{ date: string }>(blackPoint);
    const date = moment(snap.date).startOf('day');
    dates.push(date.format(dateFormatMultiCalendar));
    return dates;
  };

  cancelChange = () => {
    this.setState({
      deletedBlackPoints: [],
      deletedBlackPointsHealth: [],
      deletedBlackPointsEconomic: [],
      deletedBlackPointsSpecial: [],
      deletedDutyOffRequests: [],
      deletedDayDuties: [],
      deletedDayNightDuties: [],
      deletedNightDuties: [],
      deletedExemptions: [],
      newBlackPoints: [],
      newBlackPointsHealth: [],
      newBlackPointsEconomic: [],
      newBlackPointsSpecial: [],
      newDayDuties: [],
      newNightDuties: [],
      newDayNightDuties: [],
      comment: '',
      isFormSending: false,
      selectedType: undefined,
    });
  };

  saveChange = async () => {
    this.setState({ isFormSending: true });
    const {
      deletedBlackPoints,
      deletedBlackPointsEconomic,
      deletedBlackPointsHealth,
      deletedBlackPointsSpecial,
      deletedDutyOffRequests,
      deletedDayDuties,
      deletedDayNightDuties,
      deletedNightDuties,
      deletedExemptions,
      newBlackPoints,
      newBlackPointsEconomic,
      newBlackPointsHealth,
      newBlackPointsSpecial,
      newDayDuties,
      newDayNightDuties,
      newNightDuties,
      comment,
    } = this.state;
    const data = {
      deletedBlackPointsHoliday: deletedBlackPoints.map((elem) => moment(elem).format()),
      deletedBlackPointsHealth: deletedBlackPointsHealth.map((elem) => moment(elem).format()),
      deletedBlackPointsEconomic: deletedBlackPointsEconomic.map((elem) => moment(elem).format()),
      deletedBlackPointsSpecial: deletedBlackPointsSpecial.map((elem) => moment(elem).format()),
      deletedDutyOffRequests: deletedDutyOffRequests.map((elem) => moment(elem).format()),
      deletedDayDuties: deletedDayDuties.map((elem) => moment(elem).format()),
      deletedDayNightDuties: deletedDayNightDuties.map((elem) => moment(elem).format()), // deletedDayDuties.map((elem) => moment(elem).format()),
      deletedNightDuties: deletedNightDuties.map((elem) => moment(elem).format()), // deletedDayDuties.map((elem) => moment(elem).format()),
      deletedExemptions: deletedExemptions.map((elem) => moment(elem).format()),
      newBlackPointsHoliday: newBlackPoints.map((elem) => moment(elem).format()),
      newBlackPointsHealth: newBlackPointsHealth.map((elem) => moment(elem).format()),
      newBlackPointsEconomic: newBlackPointsEconomic.map((elem) => moment(elem).format()),
      newBlackPointsSpecial: newBlackPointsSpecial.map((elem) => moment(elem).format()),
      newDayDuties: newDayDuties.map((elem) => moment(elem).format()),
      newDayNightDuties: newDayNightDuties.map((elem) => moment(elem).format()),
      newNightDuties: newNightDuties.map((elem) => moment(elem).format()),
      comment,
      pharmacyId: this.props.pharmacyId,
    };

    try {
      await this.props.saveCalendarChange(data);
      this.cancelChange();
    } catch (e) {
      console.log('Error', e);
    }
  };

  selectPharmacy(pharmacyDetail: Instance<typeof PharmacyDetails>) {
    this.setState({ selectedPharmacy: pharmacyDetail });
    var pharmacy = this.props.pharmacyList.find((x) => x.apbNumber === pharmacyDetail.apbNumber);
    this.props.onOtherPharmacyChange!(pharmacy.pharmacyId);
  }


  getIsDirty = () => {
    const {
      newBlackPointsEconomic,
      newBlackPoints,
      newBlackPointsHealth,
      newBlackPointsSpecial,
      newDayDuties,
      newDayNightDuties,
      newNightDuties,
      deletedBlackPointsEconomic,
      deletedBlackPointsHealth,
      deletedBlackPointsSpecial,
      deletedBlackPoints,
      deletedDutyOffRequests,
      deletedDayDuties,
      deletedDayNightDuties,
      deletedNightDuties,
      comment,
    } = this.state;

    return (
      newBlackPoints.length > 0 ||
      newBlackPointsHealth.length > 0 ||
      newBlackPointsEconomic.length > 0 ||
      newBlackPointsSpecial.length > 0 ||
      newDayDuties.length > 0 ||
      newDayNightDuties.length > 0 ||
      newNightDuties.length > 0 ||
      deletedBlackPointsEconomic.length > 0 ||
      deletedBlackPointsHealth.length > 0 ||
      deletedBlackPointsSpecial.length > 0 ||
      deletedBlackPoints.length > 0 ||
      deletedDutyOffRequests.length > 0 ||
      deletedDayDuties.length > 0 ||
      deletedDayNightDuties.length > 0 ||
      deletedNightDuties.length > 0 ||
      comment !== ''
    );
  };

  canEditPharmacy = () => {
    const pharmacy =
      this.props.pharmacyId !== undefined
        ? this.props.PharmacyDetailedStore!.getById(this.props.pharmacyId)
        : this.state.pharmacy;
    return (
      this.props.AuthenticationStore!.isSuperAdmin ||
      ((pharmacy !== undefined &&
        (this.props.AuthenticationStore!.isSecretariat &&
          this.props.AuthenticationStore!.secretariatId === pharmacy!.secretariatId)) ||
        (this.props.AuthenticationStore!.isPharmacy && pharmacy!.apbNumber == this.props.AuthenticationStore!.userName))
    );
  };

  canVerifyExclusion = () => {
    return (
      (this.props.AuthenticationStore!.isSuperAdmin ||
        this.props.AuthenticationStore!.isSecretariat ||
        this.props.AuthenticationStore!.isAdmin) &&
      (this.state.newDayDuties.length > 0 ||
        this.state.newNightDuties.length > 0 ||
        this.state.newDayNightDuties.length > 0)
    );
  };

  verifyExclusion = async () => {
    this.setState({ isExclusionLoading: true });
    const dataToVerify = {
      PharmacyId: this.props.pharmacyId,
      NightDutyDates: this.state.newDayNightDuties.concat(this.state.newNightDuties),
      DayDutyDates: this.state.newDayNightDuties.concat(this.state.newDayDuties),
    };

    const result = ((await this.props.DutyAssignmentStore!.verifyExclusion(dataToVerify)) as unknown) as string[];
    const exclusions = result.map((item) => {
      const itemParts = item.split(' ');
      const date = moment(itemParts[0]).format('ddd DD/MM/YYYY');
      const type = itemParts[1] === 'DAY' ? 'jour' : 'nuit';
      const sender = itemParts[2];
      const recipient = itemParts[6];
      const recipientPh = this.props.PharmacyDetailedStore!.dataList.filter((ph) => String(ph.apbNumber) === recipient);
      const dist = Number(itemParts[7]) / 1000;
      const minDist = Number(itemParts[9]) / 1000;

      return {
        date: date,
        dutyType: type,
        pharmacyChecked: sender,
        pharmacyWithConflict: recipient + ' ' + (recipientPh[0] ? recipientPh[0].name : ''),
        distance: dist.toFixed(1),
        exclusionDistance: minDist.toFixed(1),
      } as ExclusionDetails;
    });
    this.setState({ exclusionDatas: exclusions, isExclusionLoading: false });
  };

  componentRef;

  getTransferDutiesToPharmacy = () => {
    if (this.props.pharmacyList === undefined) return undefined;
    if (this.props.UiStore!.multiCalendarFirstPharmacyId === this.props.pharmacyId)
      return this.props.pharmacyList!.find((p) => p.pharmacyId === this.props.UiStore!.multiCalendarSecondPharmacyId);
    return this.props.pharmacyList!.find((p) => p.pharmacyId === this.props.UiStore!.multiCalendarFirstPharmacyId);
  };

  getPharmacyList = () => {
    const pharmacyListProper = this.props.pharmacyList ? this.props.pharmacyList : [];
    const firstPharmacyId = this.props.UiStore!.multiCalendarFirstPharmacyId;
    if (this.props.groupIdToLimitTo === undefined || firstPharmacyId === undefined) return pharmacyListProper;
    const firstPharmacyDetails = this.props.PharmacyDetailedStore!.getById(firstPharmacyId);
    if (firstPharmacyDetails === undefined) return pharmacyListProper;
    const neighbors = firstPharmacyDetails!.pharmacyNeighbors;
    if (neighbors === undefined) return pharmacyListProper;
    return pharmacyListProper.sort((a: any, b: any) => {
      const aNeighbor = neighbors.find((n) => n.neighborId === a.pharmacyId);
      const bNeighbor = neighbors.find((n) => n.neighborId === b.pharmacyId);
      if (aNeighbor === undefined || aNeighbor.distance === undefined) return 1;
      if (bNeighbor === undefined || bNeighbor.distance === undefined) return -1;
      return aNeighbor.distance > bNeighbor.distance ? 1 : bNeighbor.distance > aNeighbor.distance ? -1 : 0;
    });
  };

  getAvailablePharmacyList = (date, apbNumber, showAlsoCurrentPharmacy) => {
    var res = this.props.PharmacyDetailedStore!.getAvailablePharmacyList(date, apbNumber, showAlsoCurrentPharmacy);
    return res;
  };

  openCandidatesDialogFromToolbox = async (date: string) => {
    this.setState({ selectedDate: date });
    if (!this.props.pharmacyDetails) {
      return;
    }
    const apbNumber = this.props.pharmacyDetails.apbNumber;

    if (!apbNumber) return;

    const availablePharmacyList = await this.getAvailablePharmacyList(date, apbNumber, true);

    this.setState({
      availableCandidatesPharmacyList: availablePharmacyList == undefined ? [] : availablePharmacyList,
      selectedCandidatesDialogDate: date,
      showCandidates: true,
    });
  };

  render() {
    // ── Perf monitoring ──────────────────────────────────────────────────────
    CalendarPerfMonitor.reset();
    this._perfRenderStart = performance.now();
    // ─────────────────────────────────────────────────────────────────────────
    const {
      bankHolidayDateList,
      dutyAssignments,
      dutyOffRequests,
      blackPointList,
      pharmacyDetails,
      translate,
      systemStatus,
      isSecretariat,
      rowCount,
      hideLegend,
      synchronizedScrolling,
      ModalStore,
      otherPharmacyBusyDates,
    } = this.props;
    const {
      deletedBlackPoints,
      deletedBlackPointsEconomic,
      deletedBlackPointsHealth,
      deletedBlackPointsSpecial,
      deletedDutyOffRequests,
      deletedDayDuties,
      deletedNightDuties,
      deletedDayNightDuties,
      deletedExemptions,
      newBlackPoints,
      newBlackPointsEconomic,
      newBlackPointsHealth,
      newBlackPointsSpecial,
      newDayDuties,
      newDayNightDuties,
      newNightDuties,
    } = this.state;
    const pharmacyFirstLineYear = this.props.fly;

    // ── [PDC] duty date arrays ───────────────────────────────────────────────
    let _t = performance.now();
    let dayDutyDates = dutyAssignments
      .filter((da) => {
        return da.duty!.dutyType !== undefined && da.duty!.dutyType.type === 'DAY';
      })
      .map((ass) => {
        return moment(ass.duty!.dutyDate.date).format(dateFormatMultiCalendar);
      });
    let nightDutyDates = dutyAssignments
      .filter((da) => {
        return da.duty!.dutyType!.type === 'NIGHT';
      })
      .map((ass) => {
        return moment(ass!.duty!.dutyDate.date).format(dateFormatMultiCalendar);
      });
    const dayNightDutyDates = dayDutyDates.filter((date) => {
      return nightDutyDates.includes(date);
    });
    dayDutyDates = dayDutyDates.filter((date) => {
      return !dayNightDutyDates.includes(date);
    });
    nightDutyDates = nightDutyDates.filter((date) => {
      return !dayNightDutyDates.includes(date);
    });
    CalendarPerfMonitor.accumulate('[PDC] duty date arrays', performance.now() - _t);

    const exchangedDutyAssignmentIds = new Set<number>(
      (this.props.exchangeHistoryList || [])
        .filter((item: any) => item.recipientId === this.props.pharmacyId)
        .map((item: any) => item.dutyAssignmentId),
    );
    const exchangeDutyDates = dutyAssignments
      .filter(
        (da) =>
          da.pharmacyId != null &&
          da.initialPharmacyId != null &&
          da.pharmacyId !== da.initialPharmacyId &&
          exchangedDutyAssignmentIds.has(da.dutyAssignmentId),
      )
      .map((da) => moment(da.duty!.dutyDate.date).format(dateFormatMultiCalendar))
      .filter((value, index, array) => array.indexOf(value) === index);

    // ── [PDC] weekend duty dates ─────────────────────────────────────────────
    _t = performance.now();
    const SaturdayDutyDates = dutyAssignments
      .map((ass) => {
        return moment(ass.duty!.dutyDate.date);
      })
      .filter((d) => d.weekday() === 5)
      .map((d) => d.format(dateFormatMultiCalendar))
      .filter((value, index, array) => array.indexOf(value) === index);

    const SundayDutyDates = dutyAssignments
      .map((ass) => {
        return moment(ass.duty!.dutyDate.date);
      })
      .filter((d) => d.weekday() === 6)
      .map((d) => d.format(dateFormatMultiCalendar))
      .filter((value, index, array) => array.indexOf(value) === index);
    CalendarPerfMonitor.accumulate('[PDC] weekend duty dates', performance.now() - _t);

    // ── [PDC] blackpoint arrays ──────────────────────────────────────────────
    _t = performance.now();
    const blackPoints1 = blackPointList
      .filter((b) => {
        return b.type !== undefined && b.type!.code === 'HOLIDAY';
      })
      .reduce(this.blackPointReducer, []);
    const blackPoints = [...Array.from(new Set<string>(blackPoints1))];

    const blackPointsEconomic1 = blackPointList
      .filter((b) => {
        return b.type !== undefined && b.type!.code === 'ECONOMIC';
      })
      .reduce(this.blackPointReducer, []);
    const blackPointsEconomic = [...Array.from(new Set<string>(blackPointsEconomic1))];

    const blackPointsHealth1 = blackPointList
      .filter((b) => {
        return b.type !== undefined && b.type!.code === 'SICK_LEAVE';
      })
      .reduce(this.blackPointReducer, []);
    const blackPointsHealth = [...Array.from(new Set<string>(blackPointsHealth1))];

    const blackPointsSpecial1 = blackPointList
      .filter((b) => {
        return b.type !== undefined && b.type!.code === 'SPECIAL';
      })
      .reduce(this.blackPointReducer, []);
    const blackPointsSpecial = [...Array.from(new Set<string>(blackPointsSpecial1))];
    CalendarPerfMonitor.accumulate('[PDC] blackpoint arrays', performance.now() - _t);

    // ── [PDC] bank holiday + BH duties + duty-off arrays ────────────────────
    _t = performance.now();
    const bankHolidayDates = bankHolidayDateList.map((elem) => moment(elem.date).format(dateFormatMultiCalendar));

    const BHDutyDates = dutyAssignments
      .map((elem) => moment(elem.duty!.dutyDate.date).format(dateFormatMultiCalendar))
      .filter((d) => bankHolidayDates.includes(d))
      .filter((value, index, array) => array.indexOf(value) === index);

    const dutyOffRequestDates = dutyOffRequests.map((elem) =>
      moment(elem.bankHolidayDate!.date).format(dateFormatMultiCalendar),
    );
    CalendarPerfMonitor.accumulate('[PDC] bankholiday+dutyoff arrays', performance.now() - _t);

    const allDaysWithMultipleActivities = () => {
      const dates = [
        ...dayDutyDates.map((date) => {
          return {
            date: moment(date).toDate(),
            typeOfActivity: DateTypeOfActivity.Duty,
            dutyType: DutyType.DAY,
          } as ConflictDate;
        }),
        ...nightDutyDates.map((date) => {
          return {
            date: moment(date).toDate(),
            typeOfActivity: DateTypeOfActivity.Duty,
            dutyType: DutyType.NIGHT,
          } as ConflictDate;
        }),
        ...dayNightDutyDates.map((date) => {
          return {
            date: moment(date).toDate(),
            typeOfActivity: DateTypeOfActivity.Duty,
            dutyType: DutyType.DAY_AND_NIGHT,
          } as ConflictDate;
        }),
        ...blackPointList.map((bpd) => {
          const snap = getSnapshot(bpd);
          return {
            date: moment(snap.date).toDate(),
            typeOfActivity: DateTypeOfActivity.BP,
          } as ConflictDate;
        }),
        ...dutyOffRequestDates.map((dord) => {
          return {
            date: moment(dord).toDate(),
            typeOfActivity: DateTypeOfActivity.DutyOff,
          } as ConflictDate;
        }),
      ].filter((ca) => ca.date.getFullYear() === pharmacyFirstLineYear);

      const nbrOfDates = new Map<number, number>();

      // Count the occurrences of each date
      dates.forEach((cd) => {
        nbrOfDates.set(cd.date.getTime(), (nbrOfDates.get(cd.date.getTime()) || 0) + 1);
      });

      // Keep the objects whose date appears at least twice
      return dates.filter((date) => (nbrOfDates.get(date.date.getTime()) || 0) >= 2);
    };

    const dirty = this.getIsDirty();

    const transferDutiesToPharmacy = async (duties: Array<Instance<typeof DutyAssignment>>) => {
      let dayDutyDates = duties
        .filter((da) => {
          return da.duty!.dutyType !== undefined && da.duty!.dutyType.type === 'DAY';
        })
        .map((ass) => {
          return moment(ass.duty!.dutyDate.date).format();
        });
      let nightDutyDates = duties
        .filter((da) => {
          return da.duty!.dutyType!.type === 'NIGHT';
        })
        .map((ass) => {
          return moment(ass!.duty!.dutyDate.date).format();
        });
      const dayNightDutyDates = dayDutyDates.filter((date) => {
        return nightDutyDates.includes(date);
      });
      dayDutyDates = dayDutyDates.filter((date) => {
        return !dayNightDutyDates.includes(date);
      });
      nightDutyDates = nightDutyDates.filter((date) => {
        return !dayNightDutyDates.includes(date);
      });
      const dataToDelete = {
        deletedBlackPointsHoliday: [],
        newBlackPointsHoliday: [],
        newBlackPoints: [],
        newBlackPointsEconomic: [],
        newBlackPointsHealth: [],
        newBlackPointsSpecial: [],
        deletedBlackPointsSpecial: [],
        newDayDuties: [],
        newNightDuties: [],
        newDayNightDuties: [],
        deletedDutyOffRequests: [],
        deletedBlackPoints: [],
        deletedBlackPointsEconomic: [],
        deletedBlackPointsHealth: [],
        deletedExemptions: [],
        comment: '',
        deletedDayDuties: dayDutyDates,
        deletedDayNightDuties: dayNightDutyDates,
        deletedNightDuties: nightDutyDates,
        pharmacyId: this.props.pharmacyId,
      };

      const dataToAdd = {
        newBlackPointsHoliday: [],
        deletedBlackPointsHoliday: [],
        newBlackPoints: [],
        newBlackPointsEconomic: [],
        newBlackPointsHealth: [],
        newBlackPointsSpecial: [],
        deletedBlackPointsSpecial: [],
        deletedDutyOffRequests: [],
        deletedBlackPoints: [],
        deletedBlackPointsEconomic: [],
        deletedBlackPointsHealth: [],
        deletedDayDuties: [],
        deletedDayNightDuties: [],
        deletedNightDuties: [],
        deletedExemptions: [],
        comment: '',
        newDayDuties: dayDutyDates,
        newDayNightDuties: dayNightDutyDates,
        newNightDuties: nightDutyDates,
        pharmacyId: this.getTransferDutiesToPharmacy().pharmacyId,
      };

      try {
        this.props.saveCalendarChange(dataToDelete);
        this.props.saveCalendarChange(dataToAdd);
        this.cancelChange();
      } catch (e) {
        console.log('Error', e);
      } finally {
        this.setState({ isTransferDutiesDialogOpen: false });
      }
    };

    const isSameDateAs = (d: string): ((value: Date, index: number, obj: Date[]) => boolean) => {
      return (fd: Date) =>
        fd.getTime() ===
        moment(d)
          .toDate()
          .getTime();
    };

    // ── [PDC] allDaysWithMultipleActivities ──────────────────────────────────
    const conflictingDatesData = CalendarPerfMonitor.measure(
      '[PDC] allDaysWithMultipleActivities',
      allDaysWithMultipleActivities,
    );

    return (
      <div>
        <ExclusionDetailsDialog
          onClose={() => this.setState({ exclusionDatas: [] })}
          detailsToDisplay={this.state.exclusionDatas}
          translate={this.props.translate}
          isLoading={this.state.isExclusionLoading}
        />
        <TransferDutiesDialog
          isOpen={this.state.isTransferDutiesDialogOpen}
          fromPharmacy={this.state.pharmacy}
          toPharmacy={this.getTransferDutiesToPharmacy()}
          transferDutiesToPharmacy={transferDutiesToPharmacy}
          onCancel={() => this.setState({ isTransferDutiesDialogOpen: false })}
          allDuties={dutyAssignments}
          toPharmacyBusyDates={otherPharmacyBusyDates === undefined ? [] : otherPharmacyBusyDates!}
        />
        <ConflictingDatesDialog
          isOpen={this.state.isConflictingDatesDialogOpen}
          onClose={() => this.setState({ isConflictingDatesDialogOpen: false })}
          translate={this.props.translate}
          conflictingDates={conflictingDatesData}
          setBlackPointDatesToDelete={(datesToDelete) => {
            this.setState({
              deletedBlackPoints: blackPoints.filter((d) => datesToDelete.find(isSameDateAs(d))),
              deletedBlackPointsEconomic: blackPointsEconomic.filter((d) => datesToDelete.find(isSameDateAs(d))),
              deletedBlackPointsHealth: blackPointsHealth.filter((d) => datesToDelete.find(isSameDateAs(d))),
              deletedBlackPointsSpecial: blackPointsSpecial.filter((d) => datesToDelete.find(isSameDateAs(d))),
            });
          }}
          setDutyAssignmentDatesToDelete={(datesToDelete) => {
            this.setState({
              deletedDayDuties: dayDutyDates.filter((d) => datesToDelete.find(isSameDateAs(d))),
              deletedDayNightDuties: dayNightDutyDates.filter((d) => datesToDelete.find(isSameDateAs(d))),
              deletedNightDuties: nightDutyDates.filter((d) => datesToDelete.find(isSameDateAs(d))),
            });
          }}
          setExemptionDatesToDelete={(datesToDelete) => {
            this.setState({
              deletedExemptions: datesToDelete.map((d) => moment(d).format(dateFormatMultiCalendar)),
            });
          }}
        />
        <LeaveRouteDirtyWrapper blockChange={dirty} />
        <MultiCalendar
          ref={(el) => (this.componentRef = el)}
          rowCount={rowCount ? rowCount : 2}
          hideLegend={hideLegend}
          synchronizedScrolling={synchronizedScrolling}
          bankHolidayDates={bankHolidayDates}
          exchangeDutyDates={exchangeDutyDates}
          dayDuties={dayDutyDates}
          nightDuties={nightDutyDates}
          dayNightDuties={dayNightDutyDates}
          saturdayDuties={SaturdayDutyDates}
          sundayDuties={SundayDutyDates}
          BHDuties={BHDutyDates}
          blackPoints={blackPoints}
          blackPointsEconomic={blackPointsEconomic}
          blackPointsHealth={blackPointsHealth}
          blackPointsSpecial={isSecretariat ? blackPointsSpecial : []}
          dutyOffRequestDates={dutyOffRequestDates}
          navigateMonthCount={12}
          newBlackPoints={newBlackPoints}
          newBlackPointsEconomic={newBlackPointsEconomic}
          newBlackPointsHealth={newBlackPointsHealth}
          newBlackPointsSpecial={newBlackPointsSpecial}
          newDayDuties={newDayDuties}
          newDayNightDuties={newDayNightDuties}
          newNightDuties={newNightDuties}
          deletedDutyOffRequests={deletedDutyOffRequests}
          deletedBlackPoints={deletedBlackPoints}
          deletedBlackPointsEconomic={deletedBlackPointsEconomic}
          deletedBlackPointsHealth={deletedBlackPointsHealth}
          deletedBlackPointsSpecial={deletedBlackPointsSpecial}
          deletedExemptions={deletedExemptions}
          // JLL
          deletedDayDuties={deletedDayDuties}
          deletedDayNightDuties={deletedDayNightDuties}
          deletedNightDuties={deletedNightDuties}
          onDatesChange={this.onDatesChange}
          onCalendarMove={this.onCalendarMove}
          systemStatus={systemStatus}
          blackPointHolidayMax={42}
          blackPointEconomicMax={10}
          annualFreeBankHolidayMax={3}
          annualFreeWeekendMax={22}
          isSecretariat={isSecretariat}
          fly={pharmacyFirstLineYear}
          sly={pharmacyFirstLineYear}
          onTypeChange={this.onTypeChange}
          selectedType={this.state.selectedType}
          separateToolbox={this.props.separateToolbox}
          pharmacyDetails={pharmacyDetails}
          pharmacyList={this.getPharmacyList()}
          pharmacyId={this.props.pharmacyId}
          onPharmacyChange={this.props.onPharmacyChange}
          onOtherPharmacyChange={this.props.onOtherPharmacyChange}
          ModalStore={ModalStore}
          groupList={this.props.GroupStore!.dataList}
          inMultiCalendar={this.props.inMultiCalendar}
          onDutyToSendToAnotherPharmacy={this.props.onDutyToSendToAnotherPharmacy}
          dutySentToAnotherPharmacy={this.props.dutySentToAnotherPharmacy}
          onDutySentConsumed={this.props.onDutySentConsumed}
          otherPharmacyBusyDates={this.props.otherPharmacyBusyDates}
          pharmacyUser={this.state.user}
          groupIdToLimitTo={this.props.groupIdToLimitTo}
          openExchangeHistoryPopup={() => this.setState({ popupExchangeHistoryVisible: true })}
          getAvailablePharmacyList={this.getAvailablePharmacyList}
          showCandidatesButton={!this.state.popupExchangeHistoryVisible}
          openCandidatesDialog={(date) => {
            this.openCandidatesDialogFromToolbox(date);
          }}
          canCandidatesDialogBeReopened={this.state.canBeReopen}
          navigateTo={this.props.ApplicationStore!.navigate}
          sharedDutiesGroupPharmacies={
            pharmacyDetails === undefined || pharmacyDetails.sharedDutiesGroupId === undefined
              ? undefined
              : this.props.PharmacyStore!.dataList.filter((p) =>
                  pharmacyDetails === undefined || pharmacyDetails.sharedDutiesGroupId === undefined
                    ? false
                    : p.sharedDutiesGroupId === pharmacyDetails.sharedDutiesGroupId &&
                      p.pharmacyId !== pharmacyDetails.pharmacyId,
                )
          }
        />
        <FormDiv>
          {!isSecretariat && rowCount !== 1 && this.state.secretariat != undefined && (
            <a
              href={
                'mailto: ' +
                (this.state.secretariat != undefined ? this.state.secretariat!.mailAddress : '') +
                '?subject=Pharmagarde - Message de la pharmacie APB n°' +
                this.props.pharmacyDetails!.apbNumber +
                '(' +
                this.props.pharmacyDetails!.name +
                ' ' +
                this.props.pharmacyDetails!.city +
                ')'
              }
            >
              Envoyer un email à votre secrétariat{' '}
              {this.state.secretariat != undefined ? this.state.secretariat!.mailAddress : ''}
            </a>
          )}
          <DivRight>
            <div>
              {!this.props.inMultiCalendar && (
                <>
                  <ReactToPrint
                    documentTitle={'PlanningDeGarde-' + moment(new Date()).format('DD-MM-YYYY') + '.pdf'}
                    trigger={() => {
                      // NOTE: could just as easily return <SomeComponent />. Do NOT pass an `onClick` prop
                      // to the root node of the returned component as it will be overwritten.
                      return <Button text={translate(TLCode.GENERAL_Print)} intent={Intent.PRIMARY} disabled={dirty} />;
                    }}
                    content={() => this.componentRef}
                  />
                  &nbsp;&nbsp;&nbsp;
                  <Button intent={Intent.NONE} disabled={false} style={{ marginRight: '35px' }}>
                    <Link
                      to={{
                        pathname: `${Page.CalendarComparisonPage}`,
                        search: '?sort=id',
                        hash: '#anchor',
                        state: { fromDashboard: true },
                      }}
                    >
                      <TranslatedMessage id={TLCode.CALENDAR_COMPARISON_PAGE} />
                    </Link>
                  </Button>
                </>
              )}
              <Button
                text={translate(TLCode.GENERAL_Cancel)}
                intent={Intent.PRIMARY}
                disabled={!dirty || this.state.isFormSending}
                onClick={() => this.cancelChange()}
              />
              <Button
                text={translate(TLCode.GENERAL_Save)}
                intent={Intent.PRIMARY}
                disabled={!dirty || !this.canEditPharmacy() || this.state.isFormSending}
                onClick={() => this.saveChange()}
                style={{ marginLeft: '5px' }}
              />
              <Button
                text={translate(TLCode.GENERAL_Verify)}
                intent={Intent.PRIMARY}
                disabled={!dirty || !this.canVerifyExclusion() || this.state.isFormSending}
                onClick={() => this.verifyExclusion()}
                style={{ marginLeft: '5px' }}
              />
              {allDaysWithMultipleActivities().length > 0 && (
                <Button
                  text={translate(TLCode.DUTIES_Date_Conflicts_Btn)}
                  intent={Intent.DANGER}
                  disabled={allDaysWithMultipleActivities().length === 0}
                  onClick={() => this.setState({ isConflictingDatesDialogOpen: true })}
                  style={{ marginLeft: '5px' }}
                  className={flashingButton}
                />
              )}
              {this.props.inMultiCalendar && (
                <Tooltip
                  content={translate(TLCode.DUTIES_TransferDialog_TransferBtn_Explained)}
                  position={Position.BOTTOM}
                  hoverOpenDelay={750}
                >
                  <Button
                    text={translate(TLCode.DUTIES_TransferDialog_TransferBtn)}
                    onClick={() => this.setState({ isTransferDutiesDialogOpen: true })}
                    disabled={
                      (this.props.inMultiCalendar &&
                        (this.props.UiStore!.multiCalendarFirstPharmacyId === undefined ||
                          this.props.UiStore!.multiCalendarSecondPharmacyId === undefined ||
                          this.props.UiStore!.multiCalendarFirstPharmacyId ===
                            this.props.UiStore!.multiCalendarSecondPharmacyId)) ||
                      (!this.props.inMultiCalendar && this.props.pharmacyId === undefined)
                    }
                    style={{ marginLeft: '5px' }}
                  />
                </Tooltip>
              )}
              <Tooltip content={translate(TLCode.PHARMACY_ExchangeHistory_popup_OPEN)} position={Position.BOTTOM}>
                <Button
                  icon={IconNames.PROPERTIES}
                  intent="primary"
                  disabled={pharmacyDetails == undefined}
                  onClick={() => this.setState({ popupExchangeHistoryVisible: true })}
                  style={{ width: '50px', marginLeft: '5px' }}
                />
              </Tooltip>
              {this.state.canBeReopen && (
                <Tooltip
                  content={this.props.translate(TLCode.DOUBLE_CALENDAR_ReopenListOfCandidates)}
                  position={Position.BOTTOM}
                >
                  <Button
                    icon={IconNames.MENU_OPEN}
                    intent={Intent.PRIMARY}
                    style={{ width: '50px', marginLeft: '5px' }}
                    onClick={() => this.setState({ showCandidates: true })}
                  />
                </Tooltip>
              )}
            </div>
          </DivRight>
        </FormDiv>
        <Dialog
          isOpen={this.state.popupExchangeHistoryVisible}
          title={translate(TLCode.PHARMACY_ExchangeHistory_popup_Title)}
          isCloseButtonShown={true}
          canOutsideClickClose={false}
          onClose={() => this.setState({ popupExchangeHistoryVisible: false })}
          style={{ width: '80vw' }}
        >
          <div className={Classes.DIALOG_BODY}>
            <PharmacyExchangeHistory exchangeHistoryList={this.props.exchangeHistoryList} pharmacy={pharmacyDetails} />
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button onClick={() => this.setState({ popupExchangeHistoryVisible: false })} intent={Intent.PRIMARY}>
                {translate(TLCode.Close)}
              </Button>
            </div>
          </div>
        </Dialog>

        <CandidatesDialog
          isOpen={this.state.showCandidates}
          translate={translate}
          onClose={(canBeReopen) => this.setState({ showCandidates: false, canBeReopen })}
          date={this.state.selectedDate!}
          pharmacy={this.props.pharmacyDetails}
          onSelectPharmacy={async (selectedPharmacy) => {
            this.selectPharmacy(selectedPharmacy!);
            return true;
          }}
          availablePharmacyList={this.state.availableCandidatesPharmacyList}
        />
      </div>
    );
  }
}

export default withTranslate(PharmacyDutiesCalendar);
